import React, { useState, useEffect } from 'react';
import {
  IonButtons,
  IonContent,
  IonHeader,
  IonItem,
  IonLabel,
  IonModal,
  IonRange,
  IonText,
  IonTitle,
  IonToolbar,
  useIonToast,
} from '@ionic/react';
import { ExploreElementType } from '../../spinnaxBase/data/explore';
import Trans from 'util/Trans';
import './CreateElementModal.css';
import DynamicButton from 'components/buttons/DynamicButton';
import { MESSAGE_ALIVE_MS, MESSAGE_POSITION } from 'util/constants';

interface CreateElementModalProps {
  isOpen: boolean;
  onDidDismiss: () => void;
  onCreateElement: (elementData: any) => Promise<void>;
}

const CreateElementModal: React.FC<CreateElementModalProps> = ({
  isOpen,
  onDidDismiss,
  onCreateElement,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    type: ExploreElementType.Event, // Default to Event since Person and Ai are not creatable
    startDate: new Date().toISOString().slice(0, 16), // ISO datetime-local format
    endDate: new Date(Date.now() + 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 16), // Tomorrow
    country: '',
    gender: '',
    ageRange: { min: 0, max: 99 }, // Default age range
    location: '',
    nationality: '',
    favorite: false,
    latitude: null as number | null,
    longitude: null as number | null,
    description: '',
    // Ranking-spezifische Felder
    isPublic: true,
    // Contest-spezifische Felder
    maxRuns: 3,
    contestRules: '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [present] = useIonToast();

  // States für Range-Werte (aus FilterEditModal übertragen)
  const [rangeValues, setRangeValues] = useState({
    lower: formData.ageRange.min,
    upper: formData.ageRange.max,
  });

  // States für die aktuellen min/max-Grenzen des Sliders
  const [sliderMin, setSliderMin] = useState(0);
  const [sliderMax, setSliderMax] = useState(99);

  // *** WICHTIG: Synchronisation zwischen ageRange und rangeValues ***
  useEffect(() => {
    setRangeValues({
      lower: formData.ageRange.min,
      upper: formData.ageRange.max,
    });
  }, [formData.ageRange]);

  // Event-Handler für Range-Änderungen während des Ziehens (onIonInput)
  const handleRangeInput = (e: CustomEvent) => {
    const { lower, upper } = e.detail.value;
    setRangeValues({ lower, upper });
  };

  // Event-Handler für Range-Änderungen beim Loslassen (onIonChange)
  const handleRangeChange = (e: CustomEvent) => {
    const { lower, upper } = e.detail.value;
    setRangeValues({ lower, upper });

    // *** WICHTIG: formData.ageRange State aktualisieren! ***
    setFormData({
      ...formData,
      ageRange: { min: lower, max: upper },
    });

    // Bereichsanpassung nur beim Loslassen
    const newMin = Math.max(
      0,
      lower <= sliderMin
        ? Math.floor(lower / 10) * 10 - 10
        : Math.floor(lower / 10) * 10
    );

    const newMax = Math.min(
      99,
      upper >= sliderMax
        ? Math.ceil(upper / 10) * 10 + 10
        : Math.ceil(upper / 10) * 10
    );

    setSliderMin(newMin);
    setSliderMax(newMax);
  };

  // Hilfsfunktion: Wandelt einen Wert in ein gültiges Date-Objekt um, oder gibt einen Default zurück
  function parseDateOrDefault(value: any, fallback: Date = new Date()): Date {
    if (!value) return fallback;
    if (value instanceof Date && !isNaN(value.getTime())) return value;
    if (typeof value === 'string') {
      const d = new Date(value);
      if (!isNaN(d.getTime())) return d;
    }
    // Firestore Timestamp?
    if (typeof value === 'object' && value.seconds !== undefined) {
      return new Date(value.seconds * 1000 + (value.nanoseconds || 0) / 1e6);
    }
    return fallback;
  }

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      // Show warning and set default name based on type
      let defaultName = '';
      if (formData.type === ExploreElementType.Event) {
        defaultName = 'Event';
      } else if (formData.type === ExploreElementType.Ranking) {
        defaultName = 'Ranking';
      } else if (formData.type === ExploreElementType.Contest) {
        defaultName = 'Contest';
      }

      present({
        message: `Warnung: Name fehlt! Automatisch auf "${defaultName}" gesetzt.`,
        duration: MESSAGE_ALIVE_MS,
        color: 'warning',
        position: MESSAGE_POSITION,
      });

      setFormData({ ...formData, name: defaultName });
      return;
    }

    // Show warnings for past dates (but allow creation)
    if (formData.type === ExploreElementType.Event && formData.startDate) {
      const selectedDate = new Date(formData.startDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day for comparison

      if (selectedDate < today) {
        present({
          message: 'Hinweis: Das gewählte Datum liegt in der Vergangenheit.',
          duration: MESSAGE_ALIVE_MS,
          color: 'warning',
          position: MESSAGE_POSITION,
        });
      }
    }

    setIsLoading(true);
    try {
      // Prepare data based on element type
      let elementData: any = {
        ...formData,
      };

      // Entferne id, falls vorhanden (darf nicht an Firebase gesendet werden)
      if ('id' in elementData) {
        delete elementData.id;
      }
      if (elementData.id === undefined) {
        delete elementData.id;
      }

      // Immer gültige Date-Objekte für startDate und endDate setzen
      if (
        formData.type === ExploreElementType.Event ||
        formData.type === ExploreElementType.Contest ||
        formData.type === ExploreElementType.Ranking
      ) {
        // Default für endDate: +1 Tag nach startDate
        const fallbackStart = new Date();
        const fallbackEnd = new Date(
          fallbackStart.getTime() + 24 * 60 * 60 * 1000
        );
        elementData.startDate = parseDateOrDefault(
          formData.startDate,
          fallbackStart
        );
        elementData.endDate = parseDateOrDefault(formData.endDate, fallbackEnd);
      }

      // Typ-spezifische Felder
      if (formData.type === ExploreElementType.Event) {
        elementData.type = ExploreElementType.Event;
        // startDate und endDate sind schon gesetzt
      } else if (formData.type === ExploreElementType.Contest) {
        elementData.type = ExploreElementType.Contest;
        elementData.maxRuns = formData.maxRuns || 3;
        elementData.contestRules = formData.contestRules || '';
        elementData.isContestActive = true;
        elementData.isParticipationOpen = true;
        elementData.participants = [];
      } else if (formData.type === ExploreElementType.Ranking) {
        elementData.type = ExploreElementType.Ranking;
        // startDate und endDate sind schon gesetzt
      }

      await onCreateElement(elementData);
      present({
        message: 'Element wurde erfolgreich erstellt!',
        duration: MESSAGE_ALIVE_MS,
        color: 'success',
        position: MESSAGE_POSITION,
      });
      setFormData({
        name: '',
        type: ExploreElementType.Event,
        startDate: new Date().toISOString().slice(0, 16),
        endDate: new Date(Date.now() + 24 * 60 * 60 * 1000)
          .toISOString()
          .slice(0, 16),
        country: '',
        gender: '',
        ageRange: { min: 0, max: 99 },
        location: '',
        nationality: '',
        favorite: false,
        description: '',
        latitude: null as number | null,
        longitude: null as number | null,
        // Ranking-spezifische Felder
        isPublic: true,
        // Contest-spezifische Felder
        maxRuns: 3,
        contestRules: '',
      });

      // Modal schließen und Element sofort anzeigen
      onDidDismiss();
    } catch (error) {
      // Spezifische Firebase-Fehlermeldungen
      let errorMessage = 'Fehler beim Erstellen des Elements';

      if (error instanceof Error) {
        // Firebase Auth Fehler
        if (error.message.includes('permission-denied')) {
          errorMessage = 'Keine Berechtigung zum Erstellen von Elementen';
        } else if (error.message.includes('network-request-failed')) {
          errorMessage =
            'Netzwerkfehler. Bitte überprüfen Sie Ihre Internetverbindung';
        } else if (error.message.includes('quota-exceeded')) {
          errorMessage =
            'Speicherplatz-Limit erreicht. Element konnte nicht erstellt werden';
        } else if (error.message.includes('unavailable')) {
          errorMessage =
            'Firebase-Service momentan nicht verfügbar. Bitte versuchen Sie es später erneut';
        } else if (error.message.includes('invalid-argument')) {
          errorMessage = 'Ungültige Daten. Bitte überprüfen Sie Ihre Eingaben';
        } else if (error.message.includes('already-exists')) {
          errorMessage = 'Ein Element mit diesem Namen existiert bereits';
        }
      }

      present({
        message: errorMessage,
        duration: MESSAGE_ALIVE_MS,
        color: 'danger',
        position: MESSAGE_POSITION,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onDidDismiss}
      className="edit-modal"
    >
      <IonHeader className="ion-no-border">
        {/* <div className="modal-image-svg">
          <img src={'/assets/images/Pic_Continent_Filter.svg'} alt="Header" />
        </div> */}
        <IonToolbar className="create-modal-toptoolbar">
          <IonButtons slot="start">
            <DynamicButton
              text={isLoading ? 'Erstelle...' : 'Element erstellen'}
              fill="clear"
              iconName="OKUpIcon"
              buttonType="icon-full"
              className="spnx-button"
              size="xlarge"
              onClick={handleSubmit}
              disabled={isLoading || !formData.name.trim()}
            />
          </IonButtons>
          <IonTitle style={{ textAlign: 'center' }}>
            <Trans i18nKey="CREATE_NEW_ELEMENT">Neues Element erstellen</Trans>
          </IonTitle>
          <IonButtons slot="end">
            <DynamicButton
              text={<Trans i18nKey="CANCEL" />}
              iconName="CancelUpIcon"
              buttonType="icon-full"
              size="xlarge"
              fill="clear"
              className="spnx-button"
              onClick={onDidDismiss}
            />
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent
        scrollY={true}
        style={{ '--background': 'transparent', '--border-colour': 0 }}
        forceOverscroll={false}
      >
        <div className="edit-modal-wrapper">
          <div className="edit-modal-group">
            <IonText>
              <Trans i18nKey="BASIC_INFO">Grunddaten</Trans>
            </IonText>
            <div className="edit-modal-innergroup">
              <IonItem>
                <IonLabel position="stacked">Name *</IonLabel>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="Name eingeben..."
                />
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">Typ</IonLabel>
                <select
                  value={formData.type}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setFormData({
                      ...formData,
                      type: e.target.value as ExploreElementType,
                    })
                  }
                  // className="custom-select"
                >
                  <option value={ExploreElementType.Event}>Event</option>
                  <option value={ExploreElementType.Ranking}>Ranking</option>
                  <option value={ExploreElementType.Contest}>Contest</option>
                </select>
              </IonItem>
            </div>
          </div>

          {/* Event-spezifische Felder */}
          {formData.type === ExploreElementType.Event && (
            <div className="edit-modal-group">
              <IonText>
                <Trans i18nKey="EVENT_DETAILS">Event-Details</Trans>
              </IonText>
              <div className="edit-modal-innergroup">
                <IonItem>
                  <IonLabel position="stacked">
                    <Trans i18nKey="START_DATE_TIME">
                      Startdatum und -zeit
                    </Trans>
                  </IonLabel>
                  <input
                    type="datetime-local"
                    value={formData.startDate || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFormData({
                        ...formData,
                        startDate: e.target.value,
                      })
                    }
                  />
                </IonItem>
                <IonItem>
                  <IonLabel position="stacked">
                    <Trans i18nKey="END_DATE_TIME">Enddatum und -zeit</Trans>
                  </IonLabel>
                  <input
                    type="datetime-local"
                    value={formData.endDate || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFormData({
                        ...formData,
                        endDate: e.target.value,
                      })
                    }
                  />
                </IonItem>
              </div>
            </div>
          )}

          {/* Contest-spezifische Felder */}
          {formData.type === ExploreElementType.Contest && (
            <div className="edit-modal-group">
              <IonText>
                <Trans i18nKey="CONTEST_DETAILS">Contest-Details</Trans>
              </IonText>
              <div className="edit-modal-innergroup">
                <IonItem>
                  <IonLabel position="stacked">
                    <Trans i18nKey="START_DATE_TIME">
                      Startdatum und -zeit
                    </Trans>
                  </IonLabel>
                  <input
                    type="datetime-local"
                    value={formData.startDate || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFormData({
                        ...formData,
                        startDate: e.target.value,
                      })
                    }
                  />
                </IonItem>
                <IonItem>
                  <IonLabel position="stacked">
                    <Trans i18nKey="END_DATE_TIME">Enddatum und -zeit</Trans>
                  </IonLabel>
                  <input
                    type="datetime-local"
                    value={formData.endDate || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFormData({
                        ...formData,
                        endDate: e.target.value,
                      })
                    }
                  />
                </IonItem>
                <IonItem>
                  <IonLabel position="stacked">
                    <Trans i18nKey="MAX_RUNS">
                      Maximale Runs pro Teilnehmer
                    </Trans>
                  </IonLabel>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={formData.maxRuns}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFormData({
                        ...formData,
                        maxRuns: parseInt(e.target.value) || 3,
                      })
                    }
                  />
                </IonItem>
                <IonItem>
                  <IonLabel position="stacked">
                    <Trans i18nKey="CONTEST_RULES">Contest-Regeln</Trans>
                  </IonLabel>
                  <textarea
                    value={formData.contestRules}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setFormData({
                        ...formData,
                        contestRules: e.target.value,
                      })
                    }
                    placeholder="Contest-Regeln eingeben..."
                    rows={3}
                    style={{ width: '100%', resize: 'vertical' }}
                  />
                </IonItem>
              </div>
            </div>
          )}

          {/* Ranking-spezifische Felder */}
          {formData.type === ExploreElementType.Ranking && (
            <div className="edit-modal-group">
              <IonText>
                <Trans i18nKey="RANKING_DETAILS">Ranking-Details</Trans>
              </IonText>
              <div className="edit-modal-innergroup">
                <IonItem>
                  <IonLabel position="stacked">
                    <Trans i18nKey="START_DATE_TIME">
                      Startdatum und -zeit
                    </Trans>
                  </IonLabel>
                  <input
                    type="datetime-local"
                    value={formData.startDate || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFormData({
                        ...formData,
                        startDate: e.target.value,
                      })
                    }
                  />
                </IonItem>
                <IonItem>
                  <IonLabel position="stacked">
                    <Trans i18nKey="END_DATE_TIME">Enddatum und -zeit</Trans>
                  </IonLabel>
                  <input
                    type="datetime-local"
                    value={formData.endDate || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFormData({
                        ...formData,
                        endDate: e.target.value,
                      })
                    }
                  />
                </IonItem>
              </div>
            </div>
          )}

          <div className="edit-modal-group">
            <IonText>
              <Trans i18nKey="LOCATION_INFO">Ortsdaten</Trans>
            </IonText>
            <div className="edit-modal-innergroup">
              <IonItem>
                <IonLabel position="stacked">Land</IonLabel>
                <input
                  type="text"
                  value={formData.country}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFormData({ ...formData, country: e.target.value })
                  }
                  placeholder="Land eingeben..."
                />
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">Nationalität</IonLabel>
                <input
                  type="text"
                  value={formData.nationality}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFormData({ ...formData, nationality: e.target.value })
                  }
                  placeholder="Nationalität eingeben..."
                />
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">Ort</IonLabel>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFormData({ ...formData, location: e.target.value })
                  }
                  placeholder="Ort eingeben..."
                />
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">Latitude</IonLabel>
                <input
                  type="number"
                  value={formData.latitude || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setFormData({
                      ...formData,
                      latitude: parseFloat(e.target.value) || null,
                    });
                  }}
                  placeholder="z.B. 52.5200"
                />
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">Longitude</IonLabel>
                <input
                  type="number"
                  value={formData.longitude || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setFormData({
                      ...formData,
                      longitude: parseFloat(e.target.value) || null,
                    });
                  }}
                  placeholder="z.B. 13.4050"
                />
              </IonItem>
            </div>
          </div>

          <div className="edit-modal-group">
            <IonText>
              <Trans i18nKey="DETAILS">Details</Trans>
            </IonText>
            <div className="edit-modal-innergroup">
              <IonItem>
                <IonLabel position="stacked">Geschlecht</IonLabel>
                <select
                  value={formData.gender}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setFormData({ ...formData, gender: e.target.value })
                  }
                  // className="custom-select"
                >
                  <option value="Male">Männlich</option>
                  <option value="Female">Weiblich</option>
                  <option value="Diverse">Diverse</option>
                  <option value="All">All</option>
                </select>
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">
                  Altersbereich: {formData.ageRange.min} -{' '}
                  {formData.ageRange.max}
                </IonLabel>
                {/* AGE SECTION - Übertragen aus FilterEditModal */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0px',
                    margin: '0',
                    width: '100%',
                  }}
                >
                  <div
                    className="range-slider"
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '100%',
                      margin: '0',
                      padding: '0',
                      position: 'relative',
                    }}
                  >
                    <div
                      className="diamond-value"
                      style={{
                        position: 'absolute',
                        left: '39px',
                        zIndex: 1,
                      }}
                    >
                      <span
                        style={{
                          transform: 'rotate(-45deg)',
                          display: 'block',
                          width: '100%',
                          textAlign: 'center',
                        }}
                      >
                        {rangeValues.lower}
                      </span>
                    </div>
                    <IonRange
                      aria-label="Dual Knobs Range"
                      dualKnobs={true}
                      min={sliderMin}
                      max={sliderMax}
                      value={rangeValues}
                      onIonInput={handleRangeInput}
                      onIonChange={handleRangeChange}
                      className="diamond-knobs"
                      pin={true}
                      pinFormatter={(value: number) => `${value}`}
                      style={{
                        width: '100%',
                        margin: '0 48px',
                      }}
                    />
                    <div
                      className="diamond-value"
                      style={{
                        position: 'absolute',
                        right: '39px',
                        zIndex: 1,
                      }}
                    >
                      <span
                        style={{
                          transform: 'rotate(-45deg)',
                          display: 'block',
                          width: '100%',
                          textAlign: 'center',
                        }}
                      >
                        {rangeValues.upper}
                      </span>
                    </div>
                  </div>
                </div>
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">Beschreibung</IonLabel>
                <textarea
                  value={formData.description}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Beschreibung eingeben..."
                  rows={3}
                  style={{ width: '100%', resize: 'vertical' }}
                />
              </IonItem>

              {/* <IonItem>
                <input
                  type="checkbox"
                  checked={formData.favorite}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFormData({ ...formData, favorite: e.target.checked })
                  }
                />
                <IonLabel>Als Favorit markieren</IonLabel>
              </IonItem> */}
            </div>
          </div>
        </div>
      </IonContent>
    </IonModal>
  );
};

export default CreateElementModal;
